import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { VacationReqBody, VacationRequest } from '../../../types/vacations';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useVacationRequestActions = (whichAction: 'vacationRequestList' | '') => {
  // query function to fetch all the vacation requests from the database declared here
  const getVacationRequestListHandler = useQuery({
    queryKey: ['vacation-request-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<VacationRequest>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/dayoff/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'vacationRequestList'),
  });

  // mutation function to create new vacation request into database declared here
  const createNewVacationRequestHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: VacationReqBody;
    }): Promise<AcademiaResponse<VacationRequest>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/dayoff/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing vacation request declared here
  const updateVacationRequestHandler = useMutation({
    mutationFn: async ({
      vacReqId,
      body,
    }: {
      vacReqId: number;
      body: VacationReqBody;
    }): Promise<AcademiaResponse<VacationRequest>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/dayoff/${vacReqId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to approve/reject vacation request declared here
  const approveOrRejectRequestHandler = useMutation({
    mutationFn: async ({
      vacReqId,
      body,
    }: {
      vacReqId: number;
      body: {
        is_approved: boolean;
        is_rejected?: boolean;
        rejected_note?: string;
      };
    }): Promise<AcademiaResponse<VacationRequest>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/dayoff/${vacReqId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation query to delete vacation request by id from the database declared here
  const deleteVacationRequestByIdHandler = useMutation({
    mutationFn: ({
      vacReqId,
    }: {
      vacReqId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/dayoff/${vacReqId}/delete/`),
  });

  return {
    getVacationRequestListHandler,
    createNewVacationRequestHandler,
    updateVacationRequestHandler,
    approveOrRejectRequestHandler,
    deleteVacationRequestByIdHandler,
  };
};

export default useVacationRequestActions;
